import { useMemo, useState } from 'react';
import { Icon } from './Icon';
import { EmptyState } from './AsyncBoundary';
import { Button } from './Button';

/**
 * The only table in the app.
 *
 * Vehicles, devices, alerts, users, trips and the audit log are all this
 * component with a different column list. A column describes itself —
 * how to get its value, how to render it, whether it sorts, how it aligns —
 * and the table handles sorting, empty state, row links and sticky headers.
 *
 *   const columns = [
 *     { key: 'reg_number', header: 'Vehicle', sortable: true,
 *       render: (row) => <VehicleCell vehicle={row} /> },
 *     { key: 'speed', header: 'Speed', align: 'right', numeric: true,
 *       value: (row) => row.last_speed_kph, render: (row) => fmt.speed(...) },
 *   ]
 *
 * Adding a column to a screen is one object. Adding a screen is one array.
 */

export function DataTable({
  columns,
  rows,
  rowKey = (row) => row.id,
  onRowClick,
  defaultSort,
  emptyState,
  dense = false,
  className = '',
  stickyHeader = true,
  // True when the server sent one page of a larger set. Sorting is then
  // switched off, because sorting fifty of eight hundred rows produces a
  // column that looks ordered and is not — the worst of the three options.
  paged = false,
}) {
  const [sort, setSort] = useState(defaultSort || null);
  const sortable = (column) => Boolean(column.sortable) && !paged;

  const sorted = useMemo(() => {
    if (!sort || paged) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column) return rows;

    const read = column.value || ((row) => row[column.key]);
    const direction = sort.direction === 'desc' ? -1 : 1;

    return [...rows].sort((a, b) => {
      const av = read(a);
      const bv = read(b);
      // Nulls always sort last, whichever way the column is pointing —
      // "no data" is not a small value.
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * direction;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * direction;
    });
  }, [rows, sort, columns, paged]);

  const toggleSort = (column) => {
    if (!sortable(column)) return;
    setSort((current) => {
      if (!current || current.key !== column.key) {
        return { key: column.key, direction: column.numeric ? 'desc' : 'asc' };
      }
      if (current.direction === 'asc') return { key: column.key, direction: 'desc' };
      return null; // third click clears the sort and restores server order
    });
  };

  if (!rows.length) {
    return emptyState ?? <EmptyState title="No results" description="Try widening your filters." />;
  }

  const cellPad = dense ? 'px-3 py-1.5' : 'px-3 py-2.5';

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="w-full border-collapse text-sm">
        <thead className={stickyHeader ? 'sticky top-0 z-10' : ''}>
          <tr className="bg-surface-2">
            {columns.map((column) => {
              const active = sort?.key === column.key;
              return (
                <th
                  key={column.key}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  className={[
                    'border-b border-line text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-3 whitespace-nowrap',
                    cellPad,
                    column.align === 'right' ? 'text-right' : 'text-left',
                    sortable(column) ? 'cursor-pointer select-none hover:text-ink-2' : '',
                  ].join(' ')}
                  onClick={() => toggleSort(column)}
                  aria-sort={
                    active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                >
                  <span
                    className={`inline-flex items-center gap-1 ${
                      column.align === 'right' ? 'flex-row-reverse' : ''
                    }`}
                  >
                    {column.header}
                    {sortable(column) && (
                      <Icon
                        name={active && sort.direction === 'asc' ? 'chevronDown' : 'chevronDown'}
                        size="xs"
                        className={[
                          'transition-transform',
                          active ? 'text-brand' : 'opacity-0 group-hover:opacity-40',
                          active && sort.direction === 'asc' ? 'rotate-180' : '',
                        ].join(' ')}
                      />
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {sorted.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === 'Enter') onRowClick(row);
                    }
                  : undefined
              }
              className={[
                'border-b border-line/70 last:border-0 transition-colors',
                onRowClick ? 'cursor-pointer hover:bg-surface-2 focus:bg-surface-2' : '',
              ].join(' ')}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={[
                    cellPad,
                    'align-middle text-ink',
                    column.align === 'right' ? 'text-right' : '',
                    column.numeric ? 'tabular-nums' : '',
                    column.nowrap !== false ? 'whitespace-nowrap' : '',
                  ].join(' ')}
                >
                  {column.render ? column.render(row) : (column.value ? column.value(row) : row[column.key]) ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The filter bar that sits above a table. One row, above the content, always —
 * so filters are in the same place on every screen.
 */
export function TableToolbar({ children, className = '' }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5 ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * The bar under a paged table.
 *
 * It states the range and the total rather than a page number, because "1–50
 * of 812" answers the question people actually have — how much is there — and
 * a page number only answers it after arithmetic. The total comes from the
 * server; the client never counts what it has not been sent.
 *
 * Hidden entirely when everything fits on one page. A pager under six rows is
 * furniture.
 */
export function Pagination({ page, onChange, unit = 'rows', className = '' }) {
  if (!page || page.total <= page.limit) return null;

  const first = page.total === 0 ? 0 : page.offset + 1;
  const last = Math.min(page.offset + page.limit, page.total);

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-2.5 ${className}`}
    >
      <span className="text-[12.5px] text-ink-3 tabular-nums">
        {first.toLocaleString()}–{last.toLocaleString()} of {page.total.toLocaleString()} {unit}
      </span>

      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          variant="secondary"
          disabled={page.offset === 0}
          onClick={() => onChange(Math.max(0, page.offset - page.limit))}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!page.hasMore}
          onClick={() => onChange(page.offset + page.limit)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
